import type { Session } from "@supabase/supabase-js";
import { useSyncExternalStore } from "react";
import { supabase } from "./supabase";

// Auth session as an external store, readable synchronously by the router's
// beforeLoad guards (no network round-trip: the session lives in storage and
// supabase-js refreshes it in the background).

let current: Session | null = null;
const listeners = new Set<() => void>();

const ready: Promise<void> = supabase
  ? supabase.auth.getSession().then(({ data }) => {
      current = data.session;
    })
  : Promise.resolve();

supabase?.auth.onAuthStateChange((_event, session) => {
  current = session;
  for (const l of listeners) l();
});

export const auth = {
  /** Resolves once the stored session has been read. */
  async getSession(): Promise<Session | null> {
    await ready;
    return current;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  get: (): Session | null => current,
};

export type Auth = typeof auth;

export function useSession(): Session | null {
  return useSyncExternalStore(auth.subscribe, auth.get, auth.get);
}

interface Claims {
  tenant_id?: unknown;
  aal?: unknown;
  mfa_required?: unknown;
}

/** Claims of the access token (no verification: the database checks the token, the app only routes on it). */
function claimsOf(session: Session | null): Claims | null {
  if (!session) return null;
  try {
    const part = session.access_token.split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Claims;
  } catch {
    return null;
  }
}

/** Reads the tenant_id claim added by custom_access_token_hook. */
export function tenantIdOf(session: Session | null): string | null {
  const claims = claimsOf(session);
  return typeof claims?.tenant_id === "string" ? claims.tenant_id : null;
}

/** A verified authenticator app (TOTP) of the signed-in user. */
export function verifiedTotpOf(session: Session | null) {
  return session?.user.factors?.find((f) => f.factor_type === "totp" && f.status === "verified") ?? null;
}

/**
 * Two-step sign-in still to do (docs/design/security.md): "verify" when the
 * user has an authenticator and the session is password-only, "enroll" when
 * the organization requires one (mfa_required claim) and none is set up yet.
 * Read from the token and the stored user: no round-trip in route guards.
 */
export function mfaStepOf(session: Session | null): "verify" | "enroll" | null {
  const claims = claimsOf(session);
  if (!session || claims?.aal === "aal2") return null;
  if (verifiedTotpOf(session)) return "verify";
  return claims?.mfa_required === true ? "enroll" : null;
}

/** The organization requires two-step sign-in of this user (mfa_required claim). */
export function mfaRequiredOf(session: Session | null): boolean {
  return claimsOf(session)?.mfa_required === true;
}

/** The session is two-factor (aal2). */
export function isAal2(session: Session | null): boolean {
  return claimsOf(session)?.aal === "aal2";
}
